import { useCallback, useEffect, useRef } from "react";

const defaultResyncTimeoutMs = 30000;

export function useManageConsentResync(resyncTimeoutMs = defaultResyncTimeoutMs): {
  armManageConsentResync: (initialConsentState: Record<string, boolean>) => void;
  clearManageConsentResync: () => void;
  isManageConsentResyncRequested: () => boolean;
  hasObservedManageConsentChange: (consentState: Record<string, boolean>) => boolean;
} {
  const isRequestedRef = useRef(false);
  const baselineSignatureRef = useRef<string | null>(null);
  const hasObservedChangeRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearManageConsentResync = useCallback((): void => {
    isRequestedRef.current = false;
    baselineSignatureRef.current = null;
    hasObservedChangeRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const armManageConsentResync = useCallback(
    (initialConsentState: Record<string, boolean>): void => {
      isRequestedRef.current = true;
      hasObservedChangeRef.current = false;
      baselineSignatureRef.current = JSON.stringify(initialConsentState);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        clearManageConsentResync();
      }, resyncTimeoutMs);
    },
    [clearManageConsentResync, resyncTimeoutMs]
  );

  const hasObservedManageConsentChange = useCallback(
    (consentState: Record<string, boolean>): boolean => {
      if (!isRequestedRef.current || baselineSignatureRef.current === null) {
        return false;
      }

      const consentSignature = JSON.stringify(consentState);
      if (consentSignature !== baselineSignatureRef.current) {
        hasObservedChangeRef.current = true;
      }

      return hasObservedChangeRef.current;
    },
    []
  );

  const isManageConsentResyncRequested = useCallback((): boolean => {
    return isRequestedRef.current;
  }, []);

  useEffect(() => {
    return (): void => {
      clearManageConsentResync();
    };
  }, [clearManageConsentResync]);

  return {
    armManageConsentResync,
    clearManageConsentResync,
    isManageConsentResyncRequested,
    hasObservedManageConsentChange,
  };
}
